import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Check, Minus, Plus, Clock, Lock, ShieldCheck, Truck, MessageCircle, ShoppingBag, Ruler, Mail,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HoodieGallery from "@/components/HoodieGallery";
import SizeGuideModal from "@/components/SizeGuideModal";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { SHOP, BUNDLE, TRUST, COMPARE, SIZE_GUIDE, PRO48 } from "@/constants/testIds";
import { trackEvent } from "@/lib/analytics";

const PRO48_CHECKOUT_ENABLED = process.env.REACT_APP_PRO48_CHECKOUT_ENABLED === "true";

const SYSTEM_IMAGE = "/pickle-wall-professional-system.png";
const COMPLETE_SYSTEM_IMAGE =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_pickle-trainer-v1/artifacts/1bxyqugt_1D945933-46C2-46BB-A63E-2B2993AF3386.png";
const PADDLE_IMAGE = "/pw700-paddles.jpg";
const PRO48_IMAGE =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_pickle-trainer-v1/artifacts/q7ez005c_17A28E49-9419-43E4-BEA3-1A862D0F5223.png";
const FOUNDER_IMAGE = "https://customer-assets.emergentagent.com/job_pickle-trainer-v1/artifacts/9lg3tdlm_image.png";

// Real catalog prices (must stay in sync with backend PRODUCT_CATALOG) — used to compute honest bundle savings.
// NOTE: PRO 48's standalone retail price is not yet approved, so it is intentionally
// excluded here to avoid an unconfirmed savings claim on the Complete System card.
const PRICE = { netEntry: 249, complete: 369, hoodie: 49.99, backpack: 39.99, paddle: 39 };

const HOODIE_COLORS = [
  { id: "Black", swatch: "#0a0a0a" },
  { id: "Blue", swatch: "#1f4fd8" },
  { id: "White", swatch: "#f5f5f5" },
  { id: "Red", swatch: "#c81e1e" },
  { id: "Pink", swatch: "#ec4899" },
];
const HOODIE_SIZES = ["S", "M", "L", "XL", "XXL"];
const hoodieFront = (color) => `/hoodie/front/hoodie-${color.toLowerCase()}-front.png`;

const COMPARE_ROWS = [
  { id: "net", label: "6 ft × 36 in Pickle Wall training net", starter: true, complete: true },
  { id: "atp", label: "ATP ZONE™ training markings", starter: true, complete: true },
  { id: "mount", label: "Heavy-duty mounting system", starter: true, complete: true },
  { id: "kitchen", label: "Kitchen-line training markers", starter: true, complete: true },
  { id: "paddles", label: "2× Pickle Wall™ PW700 paddles (carbon fiber, 16mm core)", starter: false, complete: true },
  { id: "balls", label: "PRO 48 outdoor pickleballs — 3 pack", starter: false, complete: true },
  { id: "backpack", label: "Pro Tour backpack", starter: false, complete: true },
  { id: "tape", label: "T-Rex mounting tape", starter: false, complete: true },
  { id: "guide", label: "Assembly & Training Guide", starter: false, complete: true },
];

const FAQ_ITEMS = [
  {
    q: "What's the difference between the Starter and Complete System?",
    a: "Both include the official Pickle Wall training net, ATP ZONE markings, mounting system and kitchen-line markers. The Complete System adds 2 PW700 paddles, a 3-pack of PRO 48 balls, a Pro Tour backpack, mounting tape and the training guide — everything you need in one box.",
  },
  {
    q: "Can I combine multiple items into one order?",
    a: "Yes — add any combination of systems, hoodies, paddles and accessories to your cart, then check out once. Shipping and tax are calculated together at checkout.",
  },
  {
    q: "How does hoodie sizing work?",
    a: "Our Performance Hoodie runs true to size in S–XXL. Pick your color and size on this page — the image updates instantly so you can see exactly what you're getting.",
  },
  {
    q: "Is checkout secure?",
    a: "All payments are processed securely through Stripe. We never store your card details.",
  },
  {
    q: "When will Pro 48 Pickleballs ship?",
    a: "Pro 48 Outdoor Pickleballs are in presale and marked Coming Soon — we'll let you know the moment they're ready to ship.",
  },
  {
    q: "What's your shipping & returns policy?",
    a: "Full details are on our Shipping and Return Policy pages, linked in the footer. Carriers, processing times and rates are shown at checkout before you pay.",
  },
];

function SectionLabel({ children }) {
  return <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF6B00] mb-4">{children}</div>;
}

function AddToCartButton({ testId, onClick, children, disabled }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className="w-full bg-[#FF6B00] hover:bg-[#E66000] disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold uppercase tracking-wider text-sm px-6 py-4 inline-flex items-center justify-center gap-2 transition-all hover:orange-glow"
    >
      <ShoppingBag size={14} />
      {children}
    </button>
  );
}

export default function ShopPage() {
  useEffect(() => {
    document.title = "Shop · Pickle Wall™";
    window.scrollTo(0, 0);
    trackEvent("shop_page_view", { product_category: "catalog" });
    ["net-entry", "complete-system", "hoodie", "backpack", "paddle", "balls"].forEach((id) =>
      trackEvent("shop_product_view", { product_id: id })
    );
    trackEvent("bundle_viewed", { bundle_name: "Complete Pickle Wall System" });
    trackEvent("bundle_viewed", { bundle_name: "Starter Setup" });
    trackEvent("bundle_viewed", { bundle_name: "Complete Elite" });
  }, []);

  const { addItem, setOpen: setCartOpen } = useCart();
  const [hoodieColor, setHoodieColor] = useState("White");
  const [hoodieSize, setHoodieSize] = useState("M");
  const [, setHoodieView] = useState("front");
  const [paddleQty, setPaddleQty] = useState(1);
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const sizeGuideTriggerRef = useRef(null);
  const [pro48Email, setPro48Email] = useState("");
  const [pro48Name, setPro48Name] = useState("");
  const [pro48Submitting, setPro48Submitting] = useState(false);
  const [pro48Submitted, setPro48Submitted] = useState(false);

  const selectHoodieColor = (color) => {
    setHoodieColor(color);
    trackEvent("hoodie_color_selected", { selected_color: color });
  };

  const selectHoodieSize = (size) => {
    setHoodieSize(size);
    trackEvent("hoodie_size_selected", { selected_size: size });
  };

  const submitPro48Launch = async (e) => {
    e.preventDefault();
    if (!pro48Email) return;
    setPro48Submitting(true);
    try {
      await api.post("/leads", { name: pro48Name || "Pro 48 Interest", email: pro48Email, source: "pro48-launch-list", message: "Joined PRO 48 launch list from /shop" });
      trackEvent("pro48_launch_signup", { product_name: "Pickle Wall™ PRO 48" });
      setPro48Submitted(true);
    } catch {
      toast.error("Couldn't join the launch list. Please try again.");
    } finally {
      setPro48Submitting(false);
    }
  };

  const addSystem = (sys) => {
    addItem({
      lookupKey: sys.lookupKey,
      name: sys.name,
      unitPrice: sys.price,
      quantity: 1,
      image: sys.image,
      category: "net",
    });
    trackEvent("shop_add_to_cart", { product_name: sys.name, product_id: sys.lookupKey, price: sys.price, quantity: 1 });
  };

  const addHoodie = () => {
    addItem({
      lookupKey: "pw_hoodie_v2",
      name: "Pickle Wall™ Performance Hoodie",
      unitPrice: 49.99,
      quantity: 1,
      variantNote: `${hoodieColor} · Size ${hoodieSize}`,
      image: hoodieFront(hoodieColor),
      category: "apparel",
      color: hoodieColor,
      size: hoodieSize,
    });
    trackEvent("shop_add_to_cart", { product_name: "Pickle Wall™ Performance Hoodie", product_id: "pw_hoodie_v2", price: 49.99, quantity: 1, selected_color: hoodieColor, selected_size: hoodieSize });
  };

  const addBackpack = () => {
    addItem({
      lookupKey: "pw_backpack",
      name: "Pickle Wall™ Training Backpack",
      unitPrice: 39.99,
      quantity: 1,
      image: SYSTEM_IMAGE,
      category: "accessory",
    });
    trackEvent("shop_add_to_cart", { product_name: "Pickle Wall™ Training Backpack", product_id: "pw_backpack", price: 39.99, quantity: 1 });
  };

  const addPaddle = () => {
    addItem({
      lookupKey: "pw_paddle",
      name: "Pickle Wall™ Paddle",
      unitPrice: 39,
      quantity: paddleQty,
      image: PADDLE_IMAGE,
      category: "accessory",
    });
    trackEvent("shop_add_to_cart", { product_name: "Pickle Wall™ Paddle", product_id: "pw_paddle", price: 39, quantity: paddleQty });
  };

  const addBundle = (bundle) => {
    bundle.items.forEach((it) => addItem(it));
    trackEvent("bundle_added", { bundle_name: bundle.name, cart_value: bundle.total });
    setCartOpen(true);
    toast.success(`${bundle.name} added to cart`);
  };

  const starterBundle = {
    id: "starter-setup",
    name: "Starter Setup",
    total: 249 + 49.99 + 39.99,
    items: [
      { lookupKey: "pw_net_entry", name: "Pickle Wall™ Net Training System", unitPrice: 249, quantity: 1, image: SYSTEM_IMAGE, category: "net" },
      { lookupKey: "pw_hoodie_v2", name: "Pickle Wall™ Performance Hoodie", unitPrice: 49.99, quantity: 1, variantNote: `${hoodieColor} · Size ${hoodieSize}`, image: hoodieFront(hoodieColor), category: "apparel", color: hoodieColor, size: hoodieSize },
      { lookupKey: "pw_backpack", name: "Pickle Wall™ Training Backpack", unitPrice: 39.99, quantity: 1, image: SYSTEM_IMAGE, category: "accessory" },
    ],
  };

  const eliteBundle = {
    id: "complete-elite",
    name: "Complete Elite",
    total: 369 + 49.99 + 39,
    items: [
      { lookupKey: "pw_standard", name: "Pickle Wall™ Complete Training System", unitPrice: 369, quantity: 1, image: COMPLETE_SYSTEM_IMAGE, category: "net" },
      { lookupKey: "pw_hoodie_v2", name: "Pickle Wall™ Performance Hoodie", unitPrice: 49.99, quantity: 1, variantNote: `${hoodieColor} · Size ${hoodieSize}`, image: hoodieFront(hoodieColor), category: "apparel", color: hoodieColor, size: hoodieSize },
      { lookupKey: "pw_paddle", name: "Pickle Wall™ Paddle", unitPrice: 39, quantity: 1, image: PADDLE_IMAGE, category: "accessory" },
    ],
  };

  return (
    <main id="main-content" className="bg-[#0a0a0a] text-white min-h-screen">
      <Navbar />
      <div className="pt-28 md:pt-36 pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Link
            to="/"
            data-testid="shop-back"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white mb-8 uppercase tracking-wider font-bold"
          >
            <ArrowLeft size={16} /> Back Home
          </Link>

          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF6B00] mb-3">
            Secure Checkout · Patent Pending
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-[0.95] mb-3">
            Shop Pickle Wall<span className="text-[#FF6B00]">™</span>
          </h1>
          <p className="text-white/65 text-lg max-w-2xl mb-16">
            Everything you need to turn any wall into a pickleball repetition station. Add what you need —
            check out once.
          </p>

          {/* ===== Training System Comparison ===== */}
          <SectionLabel>Choose Your System</SectionLabel>
          <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-8 mb-10 items-start">
            <div className="rounded-3xl border border-white/10 bg-[#111] overflow-hidden hidden md:block">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] text-sm" data-testid="shop-compare-table">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left font-medium text-white/50 p-4 md:p-5 w-1/2">What's Included</th>
                      <th className="text-center font-display font-black uppercase text-base p-4 md:p-5">
                        Starter<div className="text-[#FF6B00] text-lg">$249</div>
                      </th>
                      <th className="text-center font-display font-black uppercase text-base p-4 md:p-5 bg-[#FF6B00]/[0.06]">
                        Complete<div className="text-[#FF6B00] text-lg">$369</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {COMPARE_ROWS.map((row) => (
                      <tr key={row.id} data-testid={COMPARE.row(row.id)} className="border-b border-white/5 last:border-0">
                        <td className="p-4 md:p-5 text-white/75">{row.label}</td>
                        <td className="p-4 md:p-5 text-center">
                          {row.starter ? <Check size={18} className="text-[#FF6B00] mx-auto" /> : <span className="text-white/20">—</span>}
                        </td>
                        <td className="p-4 md:p-5 text-center bg-[#FF6B00]/[0.04]">
                          {row.complete ? <Check size={18} className="text-[#FF6B00] mx-auto" /> : <span className="text-white/20">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile: stacked comparison cards instead of a cramped table */}
            <div className="md:hidden space-y-4" data-testid="shop-compare-stacked">
              {[
                { key: "starter", title: "Starter — $249", rows: COMPARE_ROWS.filter((r) => r.starter) },
                { key: "complete", title: "Complete — $369", rows: COMPARE_ROWS.filter((r) => r.complete) },
              ].map((group) => (
                <div key={group.key} className="rounded-2xl border border-white/10 bg-[#111] p-5">
                  <div className="font-display font-black uppercase text-base text-[#FF6B00] mb-3">{group.title}</div>
                  <ul className="space-y-2">
                    {group.rows.map((row) => (
                      <li key={row.id} className="flex items-start gap-2 text-sm text-white/75">
                        <Check size={14} className="text-[#FF6B00] mt-0.5 shrink-0" />
                        {row.label}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div className="rounded-2xl border border-white/10 bg-[#111] p-6" data-testid={SHOP.card("net-entry")}>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-1">Starter System</div>
                <div className="font-display text-2xl font-black text-[#FF6B00] mb-3">$249</div>
                <AddToCartButton
                  testId={SHOP.buy("net-entry")}
                  onClick={() => addSystem({ lookupKey: "pw_net_entry", name: "Pickle Wall™ Net Training System", price: 249, image: SYSTEM_IMAGE })}
                >
                  Add to Cart — $249
                </AddToCartButton>
              </div>
              <div className="rounded-2xl border border-[#FF6B00] orange-glow bg-[#111] p-6" data-testid={SHOP.card("complete-system")}>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF6B00] mb-1">Most Popular · Complete System</div>
                <div className="font-display text-2xl font-black text-[#FF6B00] mb-3">$369</div>
                <AddToCartButton
                  testId={SHOP.buy("complete-system")}
                  onClick={() => addSystem({ lookupKey: "pw_standard", name: "Pickle Wall™ Complete Training System", price: 369, image: COMPLETE_SYSTEM_IMAGE })}
                >
                  Add to Cart — $369
                </AddToCartButton>
                <p className="mt-3 text-center text-xs text-white/40">One system. Everything you need to train.</p>
              </div>
            </div>
          </div>

          {/* ===== Gear & Apparel ===== */}
          <SectionLabel>Gear &amp; Apparel</SectionLabel>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10" data-testid={SHOP.card("hoodie")}>
            <HoodieGallery color={hoodieColor} onView={setHoodieView} />
            <div className="rounded-3xl border border-white/10 bg-[#111] p-6 md:p-7 flex flex-col">
              <h3 className="font-display font-black uppercase tracking-tight text-xl md:text-2xl leading-tight">
                Pickle Wall™ Performance Hoodie
              </h3>
              <div className="mt-2 font-display text-2xl font-black text-[#FF6B00]">$49.99</div>
              <p className="mt-3 text-sm text-white/65 leading-relaxed">
                Heavy-weight performance fleece with Pickle Wall / ATP ZONE branding. Choose your color and size.
              </p>

              <div className="mt-6">
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/50 mb-2">Color · {hoodieColor}</div>
                <div className="flex gap-2">
                  {HOODIE_COLORS.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      data-testid={SHOP.color(c.id.toLowerCase())}
                      onClick={() => selectHoodieColor(c.id)}
                      title={c.id}
                      aria-label={`Select ${c.id} color`}
                      style={{ backgroundColor: c.swatch }}
                      aria-pressed={hoodieColor === c.id}
                      className={`w-9 h-9 rounded-full border-2 transition-all ${
                        hoodieColor === c.id ? "border-[#FF6B00] scale-110" : "border-white/20"
                      }`}
                    />
                  ))}
                </div>
              </div>

              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/50">Size</div>
                  <button
                    type="button"
                    ref={sizeGuideTriggerRef}
                    data-testid={SIZE_GUIDE.trigger}
                    onClick={() => setSizeGuideOpen(true)}
                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#FF6B00] hover:text-[#ff8a3d]"
                  >
                    <Ruler size={11} /> Size Guide
                  </button>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {HOODIE_SIZES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      data-testid={SHOP.size(s)}
                      onClick={() => selectHoodieSize(s)}
                      aria-pressed={hoodieSize === s}
                      className={`w-11 h-10 rounded-lg text-xs font-bold uppercase transition-all ${
                        hoodieSize === s
                          ? "bg-[#FF6B00] text-black border border-[#FF6B00]"
                          : "bg-transparent text-white/80 border border-white/15 hover:border-white/40"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-auto pt-6">
                <AddToCartButton testId={SHOP.buy("hoodie")} onClick={addHoodie} disabled={!hoodieColor || !hoodieSize}>
                  Add to Cart — $49.99
                </AddToCartButton>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-16">
            <div className="rounded-3xl border border-white/10 overflow-hidden bg-[#111] flex flex-col" data-testid={SHOP.card("backpack")}>
              <div className="aspect-[4/3] w-full overflow-hidden bg-[#0a0a0a]">
                <img
                  src={SYSTEM_IMAGE}
                  alt="Pickle Wall™ branded training backpack (cropped)"
                  style={{ objectPosition: "top", transform: "scale(1.45)" }}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-display font-black uppercase tracking-tight text-xl">Training Backpack</h3>
                <div className="mt-2 font-display text-2xl font-black text-[#FF6B00]">$39.99</div>
                <p className="mt-3 text-sm text-white/65 leading-relaxed">
                  Carries paddles, balls, training accessories, hydration and everyday gear.
                </p>
                <div className="mt-auto pt-6">
                  <AddToCartButton testId={SHOP.buy("backpack")} onClick={addBackpack}>Add to Cart — $39.99</AddToCartButton>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-white/10 overflow-hidden bg-[#111] flex flex-col" data-testid={SHOP.card("paddle")}>
              <div className="aspect-[4/3] w-full overflow-hidden bg-[#0a0a0a]">
                <img src={PADDLE_IMAGE} alt="Pickle Wall™ PW700 paddle" className="w-full h-full object-contain" loading="lazy" />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-display font-black uppercase tracking-tight text-xl">PW700 Paddle</h3>
                <div className="mt-2 font-display text-2xl font-black text-[#FF6B00]">$39.00</div>
                <p className="mt-3 text-sm text-white/65 leading-relaxed">The official Pickle Wall paddle. Grab one, or two to share the reps.</p>
                <div className="mt-4">
                  <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/50 mb-2">Quantity</div>
                  <div className="flex items-center gap-3">
                    <button type="button" data-testid={SHOP.qtyDecrease} onClick={() => setPaddleQty((q) => Math.max(1, q - 1))} disabled={paddleQty <= 1} className="w-9 h-9 rounded-lg border border-white/15 disabled:opacity-40 flex items-center justify-center hover:border-white/40">
                      <Minus size={14} />
                    </button>
                    <span className="w-8 text-center font-bold">{paddleQty}</span>
                    <button type="button" data-testid={SHOP.qtyIncrease} onClick={() => setPaddleQty((q) => Math.min(2, q + 1))} disabled={paddleQty >= 2} className="w-9 h-9 rounded-lg border border-white/15 disabled:opacity-40 flex items-center justify-center hover:border-white/40">
                      <Plus size={14} />
                    </button>
                  </div>
                </div>
                <div className="mt-6">
                  <AddToCartButton testId={SHOP.buy("paddle")} onClick={addPaddle}>Add to Cart — ${(39 * paddleQty).toFixed(2)}</AddToCartButton>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-white/10 overflow-hidden bg-[#111] flex flex-col" data-testid={SHOP.card("balls")}>
              <div className="relative aspect-[4/3] w-full overflow-hidden bg-[#0a0a0a]">
                <div className="absolute top-4 left-4 z-10 text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full bg-white/10 text-white/70 border border-white/20">
                  Coming Soon
                </div>
                <img src={PRO48_IMAGE} alt="Pickle Wall™ PRO 48 outdoor pickleballs 3-pack" className="w-full h-full object-contain" loading="lazy" />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-display font-black uppercase tracking-tight text-xl">Pickle Wall™ PRO 48</h3>
                <div className="mt-2 text-xs font-bold uppercase tracking-wider text-white/50">Price &amp; ship date coming soon</div>
                <p className="mt-3 text-sm text-white/65 leading-relaxed">
                  Meet Pickle Wall™ PRO 48 — our branded outdoor pickleball, created for players who make every
                  practice count. Available in a three-ball pack.
                </p>
                <div className="mt-auto pt-6 space-y-3">
                  {PRO48_CHECKOUT_ENABLED ? (
                    <AddToCartButton testId={SHOP.buy("balls")} onClick={() => toast("PRO 48 checkout coming online shortly.")}>
                      Add to Cart — $24.99
                    </AddToCartButton>
                  ) : (
                    <>
                      <button
                        type="button"
                        data-testid={SHOP.buy("balls")}
                        disabled
                        className="w-full bg-white/10 text-white/50 font-bold uppercase tracking-wider text-sm px-6 py-4 inline-flex items-center justify-center gap-2 cursor-not-allowed"
                      >
                        <Clock size={14} /> Coming Soon
                      </button>
                      {pro48Submitted ? (
                        <p className="text-xs text-white/50 text-center">You&apos;re on the list. We&apos;ll let you know when PRO 48 drops.</p>
                      ) : (
                        <form onSubmit={submitPro48Launch} className="space-y-2">
                          <input
                            type="email"
                            required
                            value={pro48Email}
                            onChange={(e) => setPro48Email(e.target.value)}
                            placeholder="Email for launch alert"
                            data-testid={PRO48.launchEmail}
                            className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF6B00]"
                          />
                          <input
                            type="text"
                            value={pro48Name}
                            onChange={(e) => setPro48Name(e.target.value)}
                            placeholder="First name (optional)"
                            data-testid={PRO48.launchName}
                            className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF6B00]"
                          />
                          <button
                            type="submit"
                            data-testid={PRO48.launchSubmit}
                            disabled={pro48Submitting}
                            className="w-full border border-white/20 hover:border-[#FF6B00] disabled:opacity-50 text-white font-bold uppercase tracking-wider text-xs px-4 py-2.5 inline-flex items-center justify-center gap-2 rounded-lg transition-colors"
                          >
                            <Mail size={12} /> {pro48Submitting ? "Joining..." : "Join the Launch List"}
                          </button>
                        </form>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ===== Complete Your Setup ===== */}
          <SectionLabel>Complete Your Setup</SectionLabel>
          <p className="text-white/60 text-sm max-w-xl mb-6 -mt-2">
            Everything you need to train, compete, and represent Pickle Wall.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
            <div data-testid={BUNDLE.card("complete-pickle-wall-system")} className="rounded-3xl border border-[#FF6B00] orange-glow bg-[#111] p-6 md:p-7 flex flex-col">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF6B00] mb-1">Best Value</div>
              <h3 className="font-display font-black uppercase tracking-tight text-xl md:text-2xl">Complete Pickle Wall System</h3>
              <ul className="mt-4 space-y-1.5 text-sm text-white/75">
                {["Pickle Wall Training Net", "2× PW700 Paddles", "PRO 48 Pickleballs (3-pack)", "Pro Tour Backpack", "Setup / Attachment System", "Kitchen Line System"].map((label) => (
                  <li key={label} className="flex items-start gap-2">
                    <Check size={14} className="text-[#FF6B00] mt-0.5 shrink-0" /> {label}
                  </li>
                ))}
              </ul>
              <div className="mt-5 flex items-baseline gap-2">
                <span className="font-display text-3xl font-black text-[#FF6B00]">${PRICE.complete.toFixed(2)}</span>
              </div>
              <p className="mt-1 text-xs text-white/40">One system. Everything you need to train, in one box.</p>
              <div className="mt-5">
                <AddToCartButton
                  testId={BUNDLE.addAll("complete-pickle-wall-system")}
                  onClick={() => addSystem({ lookupKey: "pw_standard", name: "Pickle Wall™ Complete Training System", price: 369, image: COMPLETE_SYSTEM_IMAGE })}
                >
                  Add Complete System to Cart
                </AddToCartButton>
              </div>
            </div>

            {[starterBundle, eliteBundle].map((bundle) => (
              <div key={bundle.id} data-testid={BUNDLE.card(bundle.id)} className="rounded-3xl border border-white/10 bg-[#111] p-6 md:p-7">
                <h3 className="font-display font-black uppercase tracking-tight text-xl md:text-2xl">{bundle.name}</h3>
                <ul className="mt-4 space-y-1.5 text-sm text-white/75">
                  {bundle.items.map((it) => (
                    <li key={it.lookupKey} className="flex items-start gap-2">
                      <Check size={14} className="text-[#FF6B00] mt-0.5 shrink-0" />
                      {it.name}{it.variantNote ? ` (${it.variantNote})` : ""}
                    </li>
                  ))}
                </ul>
                <div className="mt-5 flex items-baseline gap-2">
                  <span className="font-display text-2xl font-black text-[#FF6B00]">${bundle.total.toFixed(2)}</span>
                  <span className="text-xs text-white/40">bundle subtotal</span>
                </div>
                <div className="mt-5">
                  <AddToCartButton testId={BUNDLE.addAll(bundle.id)} onClick={() => addBundle(bundle)}>
                    Add Bundle to Cart
                  </AddToCartButton>
                </div>
              </div>
            ))}
          </div>

          {/* ===== Trust ===== */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20">
            {[
              { id: "secure", icon: Lock, label: "Secure Checkout", to: null },
              { id: "patent", icon: ShieldCheck, label: "Patent-Pending Technology", to: null },
              { id: "ships", icon: Truck, label: "Ships From The USA", to: null },
              { id: "support", icon: MessageCircle, label: "Pickle Wall Support", to: "/contact" },
            ].map(({ id, icon: Icon, label, to }) => {
              const Wrapper = to ? Link : "div";
              return (
                <Wrapper key={id} {...(to ? { to } : {})} data-testid={TRUST.badge(id)} className="rounded-2xl border border-white/10 bg-[#111] p-5 flex flex-col items-center text-center gap-2 hover:border-white/25 transition-colors">
                  <Icon size={22} className="text-[#FF6B00]" />
                  <span className="text-xs font-bold uppercase tracking-wider text-white/70">{label}</span>
                </Wrapper>
              );
            })}
          </div>

          {/* ===== Founder Story ===== */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-20 rounded-3xl border border-white/10 bg-[#070707] p-6 md:p-10" data-testid="shop-founder-story">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-white/10 relative">
              <img src={FOUNDER_IMAGE} alt="Juan Carlos Diaz and his son Jamieson wearing Pickle Wall hoodies" className="absolute inset-0 w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
            </div>
            <div>
              <SectionLabel>Why We Built This</SectionLabel>
              <p className="text-lg text-white/75 leading-relaxed">
                After a serious accident and years of recovery, our founder found pickleball — but couldn't
                always find an open court or a partner to train with. So he built a wall that could.
              </p>
              <p className="mt-4 text-white/60 leading-relaxed">
                Every Pickle Wall™ product is designed around repetition — built for every player who
                refuses to wait on a partner, a court, or a comeback.
              </p>
              <Link to="/#our-story" className="mt-6 inline-flex text-sm font-bold uppercase tracking-wider text-[#FF6B00] hover:text-[#ff8a3d]">
                Read the full story →
              </Link>
            </div>
          </div>

          {/* ===== FAQ ===== */}
          <SectionLabel>Frequently Asked Questions</SectionLabel>
          <div className="rounded-3xl border border-white/10 bg-[#111] px-6 md:px-8">
            <Accordion type="single" collapsible>
              {FAQ_ITEMS.map((item, i) => (
                <AccordionItem key={item.q} value={`item-${i}`} data-testid={SHOP.faqItem(i)} className="border-white/10">
                  <AccordionTrigger className="text-base font-bold text-white hover:no-underline">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-white/65 leading-relaxed">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
      <SizeGuideModal
        open={sizeGuideOpen}
        onOpenChange={(v) => {
          setSizeGuideOpen(v);
          if (!v) window.setTimeout(() => sizeGuideTriggerRef.current?.focus(), 0);
        }}
      />
      <Footer />
    </main>
  );
}
